/**
 * Merged cells: which cell is drawn where, and how far each one runs.
 *
 * A merged cell is stored on its top-left cell, as how many columns it runs
 * across and how many rows it runs down. The cells it runs over stay in the
 * table, empty, so every row keeps as many cells as the header and everything
 * that counts positions keeps working: a merge changes what is drawn, never
 * how the table is indexed.
 *
 * The table is read as lines: the header is line 0 and body row `n` is line
 * `n + 1`. So a cell of the header can run down into the body, and the lines
 * it reaches are drawn as part of the head. A heading standing over two lines
 * of column labels makes a head two lines deep, which repeats at the top of
 * every printed page as one.
 *
 * What is stored is not trusted. A span that runs off the table, or into a
 * cell another merge already took, is cut back to what fits, reading the
 * table the way it is read, line by line and left to right, the first merge to
 * reach a cell keeping it. A document edited by hand, or written by something
 * else, still draws as a table and never as cells lying on top of each other.
 *
 * Pure, and shared by the page, the editing grid and the operations that
 * change a table's shape, so all three agree on what a merge covers.
 */

import type { TableCell, TableSection } from '@/domain/section'

/** A row of the table: its header, or a body row by index. */
export type RowRef = 'header' | number

/** A position in the table, the way a selection names it. */
export interface CellPoint {
  readonly row: RowRef
  readonly column: number
}

/**
 * The most a stored span is read as.
 *
 * Far beyond any table in a report. It only bounds what a hand-edited
 * document can ask of the layout before the table's own size cuts it back.
 */
export const MOST_SPAN = 500

/** One cell as it is drawn: where it starts and how far it runs. */
export interface DrawnCell {
  /** The line it starts on: 0 for the header, then one per body row. */
  readonly line: number
  readonly column: number
  /** How many lines it runs down, its own included. At least 1. */
  readonly rowspan: number
  /** How many columns it runs across, its own included. At least 1. */
  readonly colspan: number
}

/** A rectangle of the table, in lines and columns. Both corners are inside it. */
export interface CellArea {
  readonly top: number
  readonly left: number
  readonly bottom: number
  readonly right: number
}

/** How a table is drawn once its merges are settled. */
export interface TablePlan {
  /** The header's line and one per body row. */
  readonly lines: number
  readonly columns: number
  /** Every cell that is drawn, in reading order. */
  readonly drawn: readonly DrawnCell[]
  /** The drawn cell over a position, or undefined off the table. */
  at(line: number, column: number): DrawnCell | undefined
}

export function lineOf(row: RowRef): number {
  return row === 'header' ? 0 : row + 1
}

export function rowOf(line: number): RowRef {
  return line <= 0 ? 'header' : line - 1
}

/** How many columns the table has, counting the widest row. Never none. */
export function columnCount(section: TableSection): number {
  return Math.max(section.header.length, ...section.rows.map((row) => row.length), 1)
}

/** The cells of a line: the header's, or a body row's. */
export function lineCells(section: TableSection, line: number): readonly TableCell[] | undefined {
  return line === 0 ? section.header : section.rows[line - 1]
}

export function cellAtLine(
  section: TableSection,
  line: number,
  column: number,
): TableCell | undefined {
  return lineCells(section, line)?.[column]
}

/** A stored span, if it is one: a whole number of at least two. */
export function readSpan(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 2
    ? Math.min(value, MOST_SPAN)
    : undefined
}

/** Whether a cell asks to run over others. Says nothing about whether it may. */
export function asksToSpan(cell: TableCell | undefined): boolean {
  return (cell?.colspan ?? 1) > 1 || (cell?.rowspan ?? 1) > 1
}

export function isMerged(drawn: DrawnCell): boolean {
  return drawn.colspan > 1 || drawn.rowspan > 1
}

/**
 * Whether the header is drawn at all.
 *
 * When it holds some text, or when somebody merged its cells: a merge is a
 * decision about the head, and a head that vanished because its words had not
 * been typed yet would swallow the decision with it. An untouched empty
 * header is the table saying it has none.
 */
export function headerShown(section: TableSection): boolean {
  return section.header.some((cell) => cell.value.trim() !== '' || asksToSpan(cell))
}

/** How far a cell may run from a position, capped by the table and by 1. */
function asked(value: number | undefined, room: number): number {
  return Math.max(1, Math.min(value ?? 1, room))
}

/** The table's drawn cells, with every merge settled. */
export function planOf(section: TableSection): TablePlan {
  const columns = columnCount(section)
  const lines = section.rows.length + 1
  const owners: (DrawnCell | undefined)[] = new Array<DrawnCell | undefined>(lines * columns)
  const taken = (line: number, column: number): boolean =>
    owners[line * columns + column] !== undefined
  const drawn: DrawnCell[] = []

  for (let line = 0; line < lines; line += 1) {
    for (let column = 0; column < columns; column += 1) {
      if (taken(line, column)) continue
      const cell = cellAtLine(section, line, column)

      // Across first, stopping at the first cell a merge from above holds;
      // then down, stopping at the first line where any of those is held.
      let colspan = asked(cell?.colspan, columns - column)
      for (let step = 1; step < colspan; step += 1) {
        if (taken(line, column + step)) colspan = step
      }
      let rowspan = asked(cell?.rowspan, lines - line)
      for (let step = 1; step < rowspan; step += 1) {
        const blocked = Array.from({ length: colspan }, (_, at) => column + at).some((at) =>
          taken(line + step, at),
        )
        if (blocked) rowspan = step
      }

      const one: DrawnCell = { line, column, rowspan, colspan }
      for (let down = 0; down < rowspan; down += 1) {
        for (let across = 0; across < colspan; across += 1) {
          owners[(line + down) * columns + column + across] = one
        }
      }
      drawn.push(one)
    }
  }

  return {
    lines,
    columns,
    drawn,
    at: (line, column) =>
      line >= 0 && line < lines && column >= 0 && column < columns
        ? owners[line * columns + column]
        : undefined,
  }
}

/** Whether a position is where a drawn cell starts, rather than one it runs over. */
export function startsAt(plan: TablePlan, line: number, column: number): boolean {
  const drawn = plan.at(line, column)
  return drawn === undefined || (drawn.line === line && drawn.column === column)
}

/**
 * How many lines the head is.
 *
 * None when the header is not drawn; otherwise the header's line, and every
 * line a cell of the head runs down into, taken in turn so that a cell in a
 * line the head reached can take it further still.
 */
export function headLines(section: TableSection, plan: TablePlan = planOf(section)): number {
  if (!headerShown(section)) return 0
  let depth = 1
  for (const drawn of plan.drawn) {
    if (drawn.line >= depth) break
    depth = Math.max(depth, drawn.line + drawn.rowspan)
  }
  return depth
}

/** The drawn cells of each line, starting on it, in order. */
export function drawnByLine(plan: TablePlan): readonly (readonly DrawnCell[])[] {
  const lines: DrawnCell[][] = Array.from({ length: plan.lines }, () => [])
  for (const drawn of plan.drawn) lines[drawn.line]?.push(drawn)
  return lines
}

export function areaOfDrawn(drawn: DrawnCell): CellArea {
  return {
    top: drawn.line,
    left: drawn.column,
    bottom: drawn.line + drawn.rowspan - 1,
    right: drawn.column + drawn.colspan - 1,
  }
}

/** The rectangle two positions make, whichever corner each is. */
export function areaBetween(from: CellPoint, to: CellPoint): CellArea {
  const [a, b] = [lineOf(from.row), lineOf(to.row)]
  return {
    top: Math.min(a, b),
    left: Math.min(from.column, to.column),
    bottom: Math.max(a, b),
    right: Math.max(from.column, to.column),
  }
}

function overlaps(one: CellArea, other: CellArea): boolean {
  return (
    one.left <= other.right &&
    other.left <= one.right &&
    one.top <= other.bottom &&
    other.top <= one.bottom
  )
}

export function inside(area: CellArea, line: number, column: number): boolean {
  return line >= area.top && line <= area.bottom && column >= area.left && column <= area.right
}

/**
 * An area grown until no merged cell is cut by its edge.
 *
 * What a spreadsheet does with a selection dragged across half a merged cell,
 * and for the same reason: half a merged cell cannot be coloured, merged or
 * split, so a selection that took half of one is taken to mean all of it.
 * Clamped to the table first, so nothing outside it is ever selected.
 */
export function settleArea(plan: TablePlan, area: CellArea): CellArea | null {
  let settled: CellArea = {
    top: Math.max(0, area.top),
    left: Math.max(0, area.left),
    bottom: Math.min(plan.lines - 1, area.bottom),
    right: Math.min(plan.columns - 1, area.right),
  }
  if (settled.top > settled.bottom || settled.left > settled.right) return null

  let grown = true
  while (grown) {
    grown = false
    for (const drawn of plan.drawn) {
      const own = areaOfDrawn(drawn)
      if (!overlaps(own, settled)) continue
      const next = {
        top: Math.min(settled.top, own.top),
        left: Math.min(settled.left, own.left),
        bottom: Math.max(settled.bottom, own.bottom),
        right: Math.max(settled.right, own.right),
      }
      if (
        next.top !== settled.top ||
        next.left !== settled.left ||
        next.bottom !== settled.bottom ||
        next.right !== settled.right
      ) {
        settled = next
        grown = true
      }
    }
  }
  return settled
}

/** The drawn cells that start inside an area, in reading order. */
export function drawnIn(plan: TablePlan, area: CellArea): readonly DrawnCell[] {
  return plan.drawn.filter((drawn) => inside(area, drawn.line, drawn.column))
}

/** Whether a merge runs down across the edge between a line and the one before it. */
function crossesDown(drawn: DrawnCell, line: number): boolean {
  return drawn.line < line && drawn.line + drawn.rowspan > line
}

function crossesAcross(drawn: DrawnCell, column: number): boolean {
  return drawn.column < column && drawn.column + drawn.colspan > column
}

/**
 * Whether a line takes part in a merge running down.
 *
 * A line that does cannot be moved on its own: it would carry half of a
 * merged cell away from the other half.
 */
export function lineIsTied(plan: TablePlan, line: number): boolean {
  return plan.drawn.some(
    (drawn) => drawn.rowspan > 1 && drawn.line <= line && line < drawn.line + drawn.rowspan,
  )
}

export function columnIsTied(plan: TablePlan, column: number): boolean {
  return plan.drawn.some(
    (drawn) => drawn.colspan > 1 && drawn.column <= column && column < drawn.column + drawn.colspan,
  )
}

/** The merges a new line inserted before `line` would land inside. */
export function mergesAcrossLine(plan: TablePlan, line: number): readonly DrawnCell[] {
  return plan.drawn.filter((drawn) => crossesDown(drawn, line))
}

/** The merges a new column inserted before `column` would land inside. */
export function mergesAcrossColumn(plan: TablePlan, column: number): readonly DrawnCell[] {
  return plan.drawn.filter((drawn) => crossesAcross(drawn, column))
}
